/**
 * 共通ユーティリティ
 * - JSON dataset パース
 * - select 値の反映
 * - 2 点間距離（ハバーサイン公式）
 * - Geolocation 関連（取得オプション / エラーメッセージ）
 */

/**
 * 店舗 ID とエリア value の対応表
 * @typedef {Object.<string, string|number>} ShopAreaMap
 * @example { "1": "12", "2": "12", "3": "9" }
 */

/**
 * 1 店舗の緯度経度
 * @typedef {Object} GeoCoordinate
 * @property {number} lat - 緯度（-90 〜 90）
 * @property {number} lng - 経度（-180 〜 180）
 */

/**
 * 店舗 ID と緯度経度の対応表
 * @typedef {Object.<string, GeoCoordinate>} ShopGeoMap
 * @example { "1": { lat: 35.72, lng: 140.09 } }
 */

/**
 * data-* 属性に格納された JSON 文字列を安全にパースする。
 * 不正な JSON の場合は空オブジェクトを返す（throw しない）。
 *
 * @param {HTMLElement} el - data-* を持つ要素
 * @param {string} key - dataset のキー（キャメルケース。例: "shopAreaMap"）
 * @returns {Object} パース結果。失敗時は {}
 */
export const parseJsonDataset = (el, key) => {
    try {
        // dataset[key] は属性が無い場合 undefined になるため "{}" で代替
        return JSON.parse(el.dataset[key] || "{}");
    } catch (e) {
        // JSON が壊れていても画面を落とさない
        return {};
    }
};

/**
 * select 要素に値を反映し、change イベントを発火させる。
 * 他のリスナー（フィルタ・確認画面連動等）にも変更を伝播させるため
 * 値の代入だけでなく必ず change を dispatch する。
 *
 * @param {HTMLSelectElement} selectEl - 対象の select
 * @param {string|number} value - 設定する value
 * @returns {void}
 */
export const setSelectValue = (selectEl, value) => {
    selectEl.value = String(value);
    selectEl.dispatchEvent(new Event("change", { bubbles: true }));
};

/**
 * Geolocation API の取得オプション（最寄り店舗判定用の共通設定）
 *
 * @type {PositionOptions}
 */
export const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: false, // 精度よりレスポンス優先（最寄り店舗判定には十分）
    timeout: 10000,            // 10 秒で諦める
    maximumAge: 0,             // キャッシュ無視、常に最新位置を取得
};

/**
 * Geolocation API のエラーオブジェクトからユーザー向けメッセージを生成。
 * PERMISSION_DENIED 以外はまとめて「取得できませんでした」に統合。
 *
 * @param {GeolocationPositionError} error
 * @returns {string} alert 表示用メッセージ
 */
export const buildGeolocationErrorMessage = (error) =>
    error.code === error.PERMISSION_DENIED
        ? "位置情報の利用が許可されていません。ブラウザの設定をご確認ください。"
        : "位置情報を取得できませんでした。";

/**
 * 2 点間の距離（km）をハバーサイン公式で計算する。
 * 地球を球体と仮定するため厳密ではないが、店舗の最寄り判定には十分な精度。
 *
 * @param {number} lat1 - 1 点目の緯度（度）
 * @param {number} lng1 - 1 点目の経度（度）
 * @param {number} lat2 - 2 点目の緯度（度）
 * @param {number} lng2 - 2 点目の経度（度）
 * @returns {number} 距離（km）
 */
export const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
    // 度 → ラジアン変換
    const toRad = (deg) => (deg * Math.PI) / 180;

    const R = 6371; // 地球の平均半径（km）

    // 緯度・経度の差をラジアンで取得
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    // ハバーサイン公式の中間値
    // a = sin²(Δlat/2) + cos(lat1) * cos(lat2) * sin²(Δlng/2)
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

    // 距離 = 2 * R * arcsin(√a)
    return 2 * R * Math.asin(Math.sqrt(a));
};
